import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Edit2, X, Check, Circle } from "lucide-react";

/**
 * v6.2 Superadmin → tenant workspace with Notification Setup + Report Setup tabs.
 * URL: /superadmin/tenants/:tenantId
 */
export default function TenantWorkspace() {
  const { tenantId } = useParams();
  const nav = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [tab, setTab] = useState("notif");

  const load = async () => {
    const { data } = await api.get("/admin/tenants");
    setTenant(data.find((t) => t.tenant_id === tenantId) || null);
  };
  useEffect(() => { load(); }, [tenantId]);

  if (!tenant) return <div className="p-8 text-sm text-[var(--cw-muted)]">Loading…</div>;

  return (
    <div className="p-8 max-w-5xl" data-testid="tenant-workspace">
      <button onClick={() => nav("/superadmin")}
        className="text-xs inline-flex items-center gap-1 text-[var(--cw-muted)] hover:text-[var(--cw-primary)]">
        <ArrowLeft size={12} /> All tenants
      </button>
      <div className="text-xs text-[var(--cw-muted)] mt-2">
        Tenants / <strong>{tenant.tenant_name}</strong> / {tab === "notif" ? "Notification Setup" : "Report Setup"}
      </div>
      <h1 className="cw-h1 mt-1">{tenant.tenant_name}</h1>

      {!tenant.is_active && (
        <div className="mt-3 p-3 rounded text-xs" style={{ background: "rgba(243,191,92,0.14)", color: "#7A5900" }}>
          This tenant is inactive. Scheduled emails and notifications are not being sent. Mappings are saved and resume when the tenant is reactivated.
        </div>
      )}

      <div className="mt-4 flex gap-4 border-b" style={{ borderColor: "var(--cw-border)" }}>
        {[["notif", "Notification Setup"], ["report", "Report Setup"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`text-sm px-3 py-2 border-b-2 -mb-px ${tab === k ? "font-semibold" : "text-[var(--cw-muted)] border-transparent"}`}
            style={tab === k ? { color: "var(--cw-primary)", borderColor: "var(--cw-secondary)" } : {}}
            data-testid={`tw-tab-${k}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="mt-4">
        {tab === "notif" ? <NotifSetup tenantId={tenantId} /> : <ReportSetup tenantId={tenantId} />}
      </div>
    </div>
  );
}

function NotifSetup({ tenantId }) {
  const [rows, setRows] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [draft, setDraft] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [{ data: r }, { data: t }] = await Promise.all([
      api.get(`/v62/tenants/${tenantId}/notification-mappings`),
      api.get("/v62/shared/triggers"),
    ]);
    setRows(r); setTriggers(t);
  };
  useEffect(() => { load(); }, [tenantId]);

  const mappedTypes = new Set(rows.map((r) => r.trigger_type));
  const allMapped = triggers.length && triggers.every((t) => mappedTypes.has(t.trigger_type));

  const save = async (payload, mappingId) => {
    try {
      if (mappingId) await api.patch(`/v62/notification-mappings/${mappingId}`, payload);
      else await api.post(`/v62/tenants/${tenantId}/notification-mappings`, payload);
      toast.success("Saved"); setDraft(null); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this mapping?")) return;
    await api.delete(`/v62/notification-mappings/${id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div className="cw-card p-5" data-testid="notif-setup">
      <div className="flex items-center justify-between">
        <h3 className="cw-h3">Notification Setup</h3>
        <button
          disabled={!!draft || !!editing || allMapped}
          onClick={() => setDraft({ trigger_type: "", template_key: "" })}
          className="cw-btn-primary text-sm inline-flex items-center gap-1 disabled:opacity-40"
          title={allMapped ? "All trigger points are mapped" : ""}
          data-testid="notif-add-btn"
        >
          <Plus size={13} /> Add Mapping
        </button>
      </div>
      <table className="w-full mt-3 text-sm">
        <thead>
          <tr className="text-left text-xs text-[var(--cw-muted)]">
            <th className="py-2 w-12">S.NO</th>
            <th>Trigger point</th>
            <th>Key / Key alias</th>
            <th className="w-32">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            editing?.mapping_id === r.mapping_id ? (
              <MappingEditRow key={r.mapping_id} idx={i + 1} draft={editing}
                setDraft={setEditing} triggers={triggers} mappedTypes={mappedTypes}
                onSave={(p) => save(p, r.mapping_id)} onCancel={() => setEditing(null)} />
            ) : (
              <tr key={r.mapping_id} className="border-t" style={{ borderColor: "var(--cw-border)" }}
                data-testid={`notif-row-${r.trigger_type}`}>
                <td className="py-2">{i + 1}</td>
                <td>{triggers.find((t) => t.trigger_type === r.trigger_type)?.label || r.trigger_type}</td>
                <td>
                  <code className="text-xs">{r.template_key}</code>
                  {r.last_status === "template_not_found" && (
                    <span className="ml-2 text-[10px] text-[#B02B2B] inline-flex items-center gap-1">
                      <Circle size={6} fill="#B02B2B" /> Last send failed: template not found in ZeptoMail
                    </span>
                  )}
                </td>
                <td>
                  <button onClick={() => setEditing({ ...r })} className="cw-btn-ghost text-xs"
                    data-testid={`notif-edit-${r.trigger_type}`}><Edit2 size={12} /></button>
                  <button onClick={() => del(r.mapping_id)} className="cw-btn-ghost text-xs text-[var(--cw-error)]"
                    data-testid={`notif-del-${r.trigger_type}`}><Trash2 size={12} /></button>
                </td>
              </tr>
            )
          ))}
          {draft && (
            <MappingEditRow idx={rows.length + 1} draft={draft} setDraft={setDraft}
              triggers={triggers} mappedTypes={mappedTypes}
              onSave={(p) => save(p, null)} onCancel={() => setDraft(null)} />
          )}
        </tbody>
      </table>
    </div>
  );
}

function MappingEditRow({ idx, draft, setDraft, triggers, mappedTypes, onSave, onCancel }) {
  return (
    <tr className="border-t" style={{ borderColor: "var(--cw-border)" }}>
      <td className="py-2">{idx}</td>
      <td>
        <select value={draft.trigger_type} onChange={(e) => setDraft({ ...draft, trigger_type: e.target.value })}
          className="h-8 px-2 rounded border text-sm" style={{ borderColor: "var(--cw-border)" }}
          data-testid="notif-trigger-select">
          <option value="">Select…</option>
          {triggers.map((t) => (
            <option key={t.trigger_type} value={t.trigger_type}
              disabled={mappedTypes.has(t.trigger_type) && t.trigger_type !== draft.trigger_type}>
              {t.label}{mappedTypes.has(t.trigger_type) && t.trigger_type !== draft.trigger_type ? " (already mapped)" : ""}
            </option>
          ))}
        </select>
      </td>
      <td>
        <input value={draft.template_key} onChange={(e) => setDraft({ ...draft, template_key: e.target.value })}
          placeholder="e.g. deal_created" className="h-8 px-2 rounded border text-sm w-64 font-mono"
          style={{ borderColor: "var(--cw-border)" }} data-testid="notif-key-input" />
      </td>
      <td>
        <button onClick={() => onSave(draft)} className="cw-btn-ghost text-xs"
          data-testid="notif-save"><Check size={13} /></button>
        <button onClick={onCancel} className="cw-btn-ghost text-xs" data-testid="notif-cancel"><X size={13} /></button>
      </td>
    </tr>
  );
}

function ReportSetup({ tenantId }) {
  const [rows, setRows] = useState([]);
  const [reports, setReports] = useState([]);
  const [draft, setDraft] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [{ data: r }, { data: rp }] = await Promise.all([
      api.get(`/v62/tenants/${tenantId}/report-mappings`),
      api.get("/v62/shared/reports"),
    ]);
    setRows(r); setReports(rp);
  };
  useEffect(() => { load(); }, [tenantId]);

  const combos = new Set(rows.map((r) => `${r.report_type}|${r.report_id}`));
  const save = async (payload, mappingId) => {
    try {
      if (mappingId) await api.patch(`/v62/report-mappings/${mappingId}`, payload);
      else await api.post(`/v62/tenants/${tenantId}/report-mappings`, payload);
      toast.success("Saved"); setDraft(null); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this mapping?")) return;
    await api.delete(`/v62/report-mappings/${id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div className="cw-card p-5" data-testid="report-setup">
      <div className="flex items-center justify-between">
        <h3 className="cw-h3">Report Setup</h3>
        <button disabled={!!draft || !!editing}
          onClick={() => setDraft({ report_type: "weekly", report_id: "", template_key: "" })}
          className="cw-btn-primary text-sm inline-flex items-center gap-1 disabled:opacity-40"
          data-testid="report-add-btn"><Plus size={13} /> Add Mapping</button>
      </div>
      <table className="w-full mt-3 text-sm">
        <thead>
          <tr className="text-left text-xs text-[var(--cw-muted)]">
            <th className="py-2 w-24">Report type</th>
            <th>Which report</th>
            <th>Key / Key alias</th>
            <th className="w-32">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            editing?.mapping_id === r.mapping_id ? (
              <ReportEditRow key={r.mapping_id} draft={editing} setDraft={setEditing}
                reports={reports} combos={combos} onSave={(p) => save(p, r.mapping_id)}
                onCancel={() => setEditing(null)} />
            ) : (
              <tr key={r.mapping_id} className="border-t" style={{ borderColor: "var(--cw-border)" }}
                data-testid={`report-row-${r.report_type}-${r.report_id}`}>
                <td className="py-2 capitalize">{r.report_type}</td>
                <td>{reports.find((rp) => rp.report_id === r.report_id)?.label || r.report_id}</td>
                <td><code className="text-xs">{r.template_key}</code></td>
                <td>
                  <button onClick={() => setEditing({ ...r })} className="cw-btn-ghost text-xs"><Edit2 size={12} /></button>
                  <button onClick={() => del(r.mapping_id)} className="cw-btn-ghost text-xs text-[var(--cw-error)]"><Trash2 size={12} /></button>
                </td>
              </tr>
            )
          ))}
          {draft && (
            <ReportEditRow draft={draft} setDraft={setDraft} reports={reports} combos={combos}
              onSave={(p) => save(p, null)} onCancel={() => setDraft(null)} />
          )}
        </tbody>
      </table>
    </div>
  );
}

function ReportEditRow({ draft, setDraft, reports, combos, onSave, onCancel }) {
  return (
    <tr className="border-t" style={{ borderColor: "var(--cw-border)" }}>
      <td>
        <select value={draft.report_type} onChange={(e) => setDraft({ ...draft, report_type: e.target.value })}
          className="h-8 px-2 rounded border text-sm" style={{ borderColor: "var(--cw-border)" }}
          data-testid="report-type-select">
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
        </select>
      </td>
      <td>
        <select value={draft.report_id} onChange={(e) => setDraft({ ...draft, report_id: e.target.value })}
          className="h-8 px-2 rounded border text-sm" style={{ borderColor: "var(--cw-border)" }}
          data-testid="report-which-select">
          <option value="">Select…</option>
          {reports.map((rp) => {
            const combo = `${draft.report_type}|${rp.report_id}`;
            const disabled = combos.has(combo) && rp.report_id !== draft.report_id;
            return <option key={rp.report_id} value={rp.report_id} disabled={disabled}>
              {rp.label}{disabled ? " (already mapped)" : ""}
            </option>;
          })}
        </select>
      </td>
      <td>
        <input value={draft.template_key} onChange={(e) => setDraft({ ...draft, template_key: e.target.value })}
          placeholder="e.g. weekly_pipeline_dashboard"
          className="h-8 px-2 rounded border text-sm w-64 font-mono"
          style={{ borderColor: "var(--cw-border)" }} data-testid="report-key-input" />
      </td>
      <td>
        <button onClick={() => onSave(draft)} className="cw-btn-ghost text-xs"
          data-testid="report-save"><Check size={13} /></button>
        <button onClick={onCancel} className="cw-btn-ghost text-xs" data-testid="report-cancel"><X size={13} /></button>
      </td>
    </tr>
  );
}
